import { BookOpen, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useReaderSettings } from '../../../hooks/useReaderSettings';
import { LayoutMode, PageAnimation, PageMargin, TapZoneMode } from '../types';
import { controlClasses } from './controls';
import { useTranslation } from '../../../i18n/useTranslation';

interface LayoutPanelProps {
  isOpen: boolean;
  onClose: () => void;
  asPanel?: boolean;
  /** Optional — shown at top of panel when available. */
  currentPage?: number;
  /** Optional — shown at top of panel when available. */
  totalPages?: number;
}


function SegmentedGroup<T extends number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: T; label: string; hint?: string }>;
  value: T;
  onChange: (next: T) => void;
}) {
  const selected = options.find((o) => o.value === value);
  return (
    <div>
      <div className={controlClasses.fieldLabel}>{label}</div>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
        {options.map((opt) => (
          <button
            key={opt.value}
            role="radio"
            aria-checked={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={`py-1.5 px-3 text-xs font-medium rounded-md border transition-all ${
              value === opt.value
                ? 'border-primary-600 bg-primary-50 text-primary-700'
                : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {selected?.hint && (
        <p className="text-[11px] text-neutral-500 mt-1 leading-snug">{selected.hint}</p>
      )}
    </div>
  );
}

export default function LayoutPanel({ isOpen, onClose, asPanel = false, currentPage, totalPages }: LayoutPanelProps) {
  const { t } = useTranslation();
  const { settings, updateSettings, isUpdating } = useReaderSettings();

  const MODE_OPTIONS: Array<{ value: LayoutMode; label: string; hint?: string }> = [
    { value: LayoutMode.Auto, label: t('App.Reader.Layout.Mode.Auto'), hint: t('App.Reader.Layout.Mode.AutoHint') },
    { value: LayoutMode.Single, label: t('App.Reader.Layout.Mode.Single') },
    { value: LayoutMode.Spread, label: t('App.Reader.Layout.Mode.Spread'), hint: t('App.Reader.Layout.Mode.SpreadHint') },
    { value: LayoutMode.Scrolling, label: t('App.Reader.Layout.Mode.Scrolling'), hint: t('App.Reader.Layout.Mode.ScrollingHint') },
  ];

  const ANIM_OPTIONS: Array<{ value: PageAnimation; label: string }> = [
    { value: PageAnimation.Slide, label: t('App.Reader.Layout.Animation.Slide') },
    { value: PageAnimation.Fade, label: t('App.Reader.Layout.Animation.Fade') },
    { value: PageAnimation.None, label: t('App.Reader.Layout.Animation.None') },
  ];

  const MARGIN_OPTIONS: Array<{ value: PageMargin; label: string }> = [
    { value: PageMargin.Narrow, label: t('App.Reader.Layout.Margin.Narrow') },
    { value: PageMargin.Medium, label: t('App.Reader.Layout.Margin.Medium') },
    { value: PageMargin.Wide, label: t('App.Reader.Layout.Margin.Wide') },
  ];

  const TAPZONE_OPTIONS: Array<{ value: TapZoneMode; label: string; hint?: string }> = [
    { value: TapZoneMode.Auto, label: t('App.Reader.Layout.TapZone.Auto'), hint: t('App.Reader.Layout.TapZone.AutoHint') },
    { value: TapZoneMode.On, label: t('App.Reader.Layout.TapZone.On') },
    { value: TapZoneMode.Off, label: t('App.Reader.Layout.TapZone.Off') },
  ];
  const dialogRef = useRef<HTMLDivElement>(null);

  const [layoutMode, setLayoutMode] = useState<LayoutMode>(LayoutMode.Auto);
  const [pageAnimation, setPageAnimation] = useState<PageAnimation>(PageAnimation.Slide);
  const [pageMargin, setPageMargin] = useState<PageMargin>(PageMargin.Medium);
  const [tapZoneMode, setTapZoneMode] = useState<TapZoneMode>(TapZoneMode.Auto);

  useEffect(() => {
    if (settings) {
      setLayoutMode(settings.layoutMode);
      setPageAnimation(settings.pageAnimation);
      setPageMargin(settings.pageMargin);
      setTapZoneMode(settings.tapZoneMode);
    }
  }, [settings]);

  const handleSave = async () => {
    await updateSettings({ layoutMode, pageAnimation, pageMargin, tapZoneMode });
    onClose();
  };

  const handleReset = () => {
    setLayoutMode(LayoutMode.Auto);
    setPageAnimation(PageAnimation.Slide);
    setPageMargin(PageMargin.Medium);
    setTapZoneMode(TapZoneMode.Auto);
  };

  if (!isOpen) return null;

  const content = (
    <>
      <div className="px-4 py-3 space-y-4">
        {typeof currentPage === 'number' && typeof totalPages === 'number' && (
          <div className="text-[11px] text-neutral-500">
            {t('App.Reader.Layout.PageIndicator', { page: currentPage, total: totalPages })}
          </div>
        )}
        <SegmentedGroup label={t('App.Reader.Layout.Title')} options={MODE_OPTIONS} value={layoutMode} onChange={setLayoutMode} />
        <SegmentedGroup label={t('App.Reader.Layout.AnimationTitle')} options={ANIM_OPTIONS} value={pageAnimation} onChange={setPageAnimation} />
        <SegmentedGroup label={t('App.Reader.Layout.MarginTitle')} options={MARGIN_OPTIONS} value={pageMargin} onChange={setPageMargin} />
        <SegmentedGroup label={t('App.Reader.Layout.TapZoneTitle')} options={TAPZONE_OPTIONS} value={tapZoneMode} onChange={setTapZoneMode} />

        <p className="text-[11px] text-neutral-500 leading-snug border-t border-neutral-200 pt-2">
          {t('App.Reader.Layout.TapZone.Description')}
        </p>
      </div>

      <div className="sticky bottom-0 bg-neutral-50 border-t border-neutral-200 px-4 py-3 flex justify-between items-center gap-2">
        <button
          onClick={handleReset}
          className="px-2 py-1.5 text-[13px] font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors whitespace-nowrap"
        >
          {t('App.Reader.Layout.Reset')}
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className={controlClasses.btnSecondary}>{t('App.Reader.Layout.Cancel')}</button>
          <button onClick={handleSave} disabled={isUpdating} className={controlClasses.btnPrimary}>
            {isUpdating ? t('App.Reader.Layout.Saving') : t('App.Reader.Layout.Save')}
          </button>
        </div>
      </div>
    </>
  );

  if (asPanel) return content;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[1100] flex items-center justify-center p-4"
      role="presentation"
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="layout-panel-title"
        tabIndex={-1}
      >
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary-600" aria-hidden="true" />
            <h2 id="layout-panel-title" className="text-xl font-semibold text-neutral-800">{t('App.Reader.Layout.Title')}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full transition-colors" aria-label={t('App.Reader.Layout.Close')}>
            <X className="w-5 h-5 text-neutral-500" aria-hidden="true" />
          </button>
        </div>
        {content}
      </div>
    </div>
  );
}
